<template>
  <div class="share-button-container">
    <button class="share-button" :class="{ active: isOpen }" @click="toggleDropdown" :disabled="loading || !isOwner">
      <QIcon :path="isPublic ? mdiEarth : mdiLock" :size="16" />
      <span class="share-text">{{ isPublic ? 'Shared' : 'Private' }}</span>
      <QIcon :path="mdiChevronDown" :size="14" />
    </button>

    <Transition name="dropdown">
      <div v-if="isOpen" class="share-dropdown">
        <div class="dropdown-content">
          <div class="toggle-row">
            <div class="toggle-info">
              <div class="toggle-label">Anyone with the link</div>
              <div class="toggle-sublabel">Anyone on the internet with the link can view</div>
            </div>
            <button
              class="toggle-switch"
              :class="{ active: isPublic }"
              @click="toggleSharing"
              :disabled="loading"
              role="switch"
              :aria-checked="isPublic"
            >
              <div class="toggle-thumb"></div>
            </button>
          </div>

          <div v-if="isPublic" class="divider"></div>

          <button v-if="isPublic" class="copy-link-button" @click="copyLink" :disabled="loading">
            <QIcon :path="mdiContentCopy" :size="16" />
            <span>Copy link</span>
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { mdiChevronDown, mdiContentCopy, mdiEarth, mdiLock } from '@mdi/js';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { fetchWithAuth } from '../services/api';
import QIcon from './QIcon.vue';

interface Props {
  projectId: string;
  initialIsPublic: boolean;
  isOwner: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:isPublic', value: boolean): void;
  (e: 'toast', message: string): void;
}>();

const isPublic = ref(props.initialIsPublic);
const loading = ref(false);
const isOpen = ref(false);

// Sync local ref with prop changes
watch(
  () => props.initialIsPublic,
  newValue => {
    isPublic.value = newValue;
  }
);

function toggleDropdown() {
  isOpen.value = !isOpen.value;
}

function closeDropdown() {
  isOpen.value = false;
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  const container = target.closest('.share-button-container');

  // Close if clicking outside the container, or inside container but outside button/dropdown
  if (!container) {
    closeDropdown();
  } else if (isOpen.value) {
    // Check if click is on button or dropdown content
    const isButton = target.closest('.share-button');
    const isDropdown = target.closest('.share-dropdown');

    if (!isButton && !isDropdown) {
      closeDropdown();
    }
  }
}

async function toggleSharing() {
  loading.value = true;
  try {
    const response = await fetchWithAuth(`/api/projects/${props.projectId}/sharing`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isPublic: !isPublic.value }),
    });

    if (!response.ok) {
      throw new Error('Failed to update sharing');
    }

    const data = await response.json();
    isPublic.value = data.isPublic;
    emit('update:isPublic', data.isPublic);

    if (data.isPublic) {
      await copyLink();
    }
  } catch (error) {
    console.error('Failed to toggle sharing:', error);
    emit('toast', 'Failed to update sharing settings');
  } finally {
    loading.value = false;
  }
}

async function copyLink() {
  const url = `${window.location.origin}/edit/${props.projectId}`;
  try {
    await navigator.clipboard.writeText(url);
    emit('toast', 'Link copied!');
  } catch (error) {
    console.error('Failed to copy link:', error);
    emit('toast', 'Failed to copy link');
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.share-button-container {
  position: relative;
}

.share-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  background: transparent;
  border: 1px solid var(--color-border, #e2e8f0);
  cursor: pointer;
  transition:
    background-color 0.2s,
    border-color 0.2s,
    color 0.2s;
  font-family: inherit;
  transform: none !important;
  box-shadow: none !important;
}

.share-button:hover {
  background: rgba(0, 0, 0, 0.03);
  border-color: var(--color-accent);
  color: var(--color-accent);
  transform: none !important;
  box-shadow: none !important;
}

.share-button.active {
  background: rgba(0, 0, 0, 0.03);
  border-color: var(--color-accent);
  transform: none !important;
  box-shadow: none !important;
}

.share-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.share-text {
  font-size: 0.875rem;
  font-weight: 500;
}

.share-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: white;
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 280px;
}

.dropdown-content {
  padding: 12px;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.toggle-info {
  flex: 1;
}

.toggle-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: 2px;
}

.toggle-sublabel {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  line-height: 1.4;
}

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  background: var(--color-border, #e2e8f0);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  flex-shrink: 0;
}

.toggle-switch.active {
  background: var(--color-accent);
}

.toggle-switch:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch.active .toggle-thumb {
  transform: translateX(20px);
}

.divider {
  height: 1px;
  background: var(--color-border, #e2e8f0);
  margin: 12px 0;
}

.copy-link-button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  background: transparent;
  border: 1px solid var(--color-border, #e2e8f0);
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  font-weight: 500;
}

.copy-link-button:hover {
  background: rgba(0, 0, 0, 0.03);
  border-color: var(--color-accent);
  color: var(--color-accent);
}

.copy-link-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.15s ease;
  transform-origin: top left;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-4px);
}
</style>
